import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem } from '../../Redux/Menu/menuSlice';
import { MenuOptionGroup } from '../../Redux/MenuOption/types';
import {
  uploadMenuImage,
  addMenu,
  deleteMenu,
  reorderMenus,
  fetchMenus,
  fetchMenuGroups,
  saveMenuGroup,
  detachMenuGroup,
  updateMenu,
  updateMenuSuspension
} from '../../Redux/Menu/menuApi';
import { formatPrice } from '../../Formatter/formatPrice';
import { AppDispatch, RootState } from '../../Redux/Store';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDraggable } from '../../hooks/useDraggable';
import { reorderMenusLocally } from '../../Redux/Menu/menuSlice';
import {
  Box,
  Button,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Select,
  MenuItem as MuiMenuItem,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import BlockIcon from '@mui/icons-material/Block';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

/** "YYYY-MM-DDTHH:mm(:ss)" を「M/D HH:mm」表示にする */
const formatSuspendedUntil = (until: string): string => {
  const d = new Date(until);
  if (Number.isNaN(d.getTime())) return until;
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** 現在時刻を datetime-local の値形式 "YYYY-MM-DDTHH:mm" で返す */
const nowAsDatetimeLocal = (): string => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const handleImageChange = async (
  file: File,
  store_id: string,
  dispatch: AppDispatch,
  setImageUrl: React.Dispatch<React.SetStateAction<string>>,
  currentImageUrl?: string | null
) => {
  const resultAction = await dispatch(uploadMenuImage({ file, storeId: store_id, currentImageUrl }));
  if (uploadMenuImage.fulfilled.match(resultAction)) {
    const { imageUrl } = resultAction.payload;
    setImageUrl(imageUrl);
  }
};

interface MenuManagementProps {
  newMenuName: string;
  newMenuPrice: number | '';
  setNewMenuName: React.Dispatch<React.SetStateAction<string>>;
  setNewMenuPrice: React.Dispatch<React.SetStateAction<number | ''>>;
}

interface DraggableMenuItemProps {
  menu: MenuItem;
  handleDeleteMenuInternal: (id: string) => void;
  menuGroups: MenuOptionGroup[];
  onSaveMenuGroup: (menuId: string, groupId: string) => void;
  onDetachMenuGroup: (menuId: string, menuOptionGroupId: string) => void;
  onUpdateMenu?: (updatedMenu: MenuItem) => void;
  onToggleSuspension: (menu: MenuItem) => void;
  dispatch: AppDispatch;
  store_id: string | undefined;
  handleOpenDeleteModal: (id: string) => void;
}

interface SuspensionDialogProps {
  menu: MenuItem | null;
  onClose: () => void;
  onConfirm: (suspendedUntil: string | null) => void;
}

/** 受注停止の恒久/日時指定を選ぶダイアログ（FE ADR 0054） */
const SuspensionDialog: React.FC<SuspensionDialogProps> = ({ menu, onClose, onConfirm }) => {
  const [mode, setMode] = useState<'permanent' | 'until'>('permanent');
  const [until, setUntil] = useState<string>('');

  useEffect(() => {
    if (menu) {
      setMode(menu.suspendedUntil ? 'until' : 'permanent');
      setUntil(menu.suspendedUntil ? menu.suspendedUntil.slice(0, 16) : '');
    }
  }, [menu]);

  return (
    <Dialog open={!!menu} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>受注を停止する</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          「{menu?.name}」はお客様の注文画面で「売り切れ」と表示され、注文できなくなります。
        </DialogContentText>
        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value as 'permanent' | 'until')}
        >
          <FormControlLabel
            value="permanent"
            control={<Radio />}
            label="再開するまで停止する"
          />
          <FormControlLabel
            value="until"
            control={<Radio />}
            label="日時を指定して停止する（指定日時に自動再開）"
          />
        </RadioGroup>
        {mode === 'until' && (
          <TextField
            fullWidth
            label="受注を再開する日時"
            type="datetime-local"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            margin="normal"
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: nowAsDatetimeLocal() }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          キャンセル
        </Button>
        <Button
          onClick={() => onConfirm(mode === 'until' ? until : null)}
          color="error"
          variant="contained"
          disabled={mode === 'until' && !until}
        >
          停止する
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DraggableMenuItem: React.FC<DraggableMenuItemProps> = ({
  menu,
  handleDeleteMenuInternal,
  menuGroups,
  onSaveMenuGroup,
  onDetachMenuGroup,
  onUpdateMenu,
  onToggleSuspension,
  dispatch,
  store_id,
  handleOpenDeleteModal,
}) => {
  const { setNodeRef, setActivatorNodeRef, style, attributes, listeners } = useDraggable(
    menu.id,
    { direction: 'vertical' }
  );

  const [expanded, setExpanded] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(menu.menuOptionGroupId || '');
  const [isEditing, setIsEditing] = useState(false);
  const [editedMenu, setEditedMenu] = useState<MenuItem>(menu);
  const [editImageUrl, setEditImageUrl] = useState<string>('');
  const [editPreview, setEditPreview] = useState<string>('');

  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const handleEditStart = () => {
    setEditedMenu(menu);
    setEditImageUrl(menu.imageUrl || '');
    setEditPreview(menu.imageUrl ? process.env.REACT_APP_CLOUDFRONT_URL + menu.imageUrl : '');
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedMenu(menu);
    setEditImageUrl('');
    setEditPreview('');
  };

  const handleGroupSelect = (event: SelectChangeEvent) => {
    setSelectedGroupId(event.target.value);
  };

  const handleSaveGroup = () => {
    if (selectedGroupId) {
      onSaveMenuGroup(menu.id, selectedGroupId);
      setExpanded(false);
    }
  };

  const handleDetachGroup = () => {
    if (menu.menuOptionGroupId) {
      onDetachMenuGroup(menu.id, menu.menuOptionGroupId);
      setSelectedGroupId('');
      setExpanded(false);
    }
  };

  const onEditDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && store_id) {
      setEditPreview(URL.createObjectURL(file));
      // 差し替え時は既存の画像キーへ上書き（同一メニューなら画像 ID 不変、ADR 0065）
      handleImageChange(file, store_id, dispatch, setEditImageUrl, editImageUrl || menu.imageUrl);
    }
  };

  const onEditFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && store_id) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to S3（差し替え時は既存キーへ上書き、ADR 0065）
      await handleImageChange(file, store_id, dispatch, setEditImageUrl, editImageUrl || menu.imageUrl);
    }
  };

  const handleClearImage = () => {
    setEditImageUrl('');
    setEditPreview('');
  };

  return (
    <ListItem
      component="li"
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        mb: 1,
        borderRadius: 1,
        boxShadow: 1,
        width: '100%',
        flexDirection: 'column',
      }}
    >
      {isEditing ? (
        <Box sx={{ width: '100%', p: 1 }}>
          <TextField
            fullWidth
            label="メニュー名"
            value={editedMenu.name}
            onChange={(e) => setEditedMenu({ ...editedMenu, name: e.target.value })}
            margin="dense"
            size="small"
          />
          <TextField
            fullWidth
            label="価格（税抜・円）"
            type="text"
            value={editedMenu.price}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              setEditedMenu({ ...editedMenu, price: value === '' ? 0 : Number(value) });
            }}
            margin="dense"
            size="small"
            helperText="税抜金額を入力してください"
          />

          {/* Image upload area */}
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 1,
              p: 2,
              mt: 2,
              mb: 2,
              textAlign: 'center',
              cursor: 'pointer',
            }}
            onDrop={onEditDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              type="file"
              accept="image/*"
              onChange={onEditFileChange}
              style={{ display: 'none' }}
              id={`menu-edit-image-input-${menu.id}`}
              value=""
            />
            <label htmlFor={`menu-edit-image-input-${menu.id}`}>
              {editPreview ? (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={editPreview}
                    alt="Preview"
                    sx={{ maxWidth: '100%', maxHeight: 200 }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                      }
                    }}
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClearImage();
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Box>
              ) : (
                <Typography>
                  画像をドラッグ＆ドロップまたはクリックしてアップロード
                </Typography>
              )}
            </label>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleEditCancel}
              sx={{ mr: 1 }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const updatedMenu = {
                  ...editedMenu,
                  imageUrl: editImageUrl || null
                };
                onUpdateMenu?.(updatedMenu);
                setIsEditing(false);
              }}
            >
              保存
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
          <Box
            ref={setActivatorNodeRef}
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
            {...attributes}
            {...listeners}
          >
            <DragIndicatorIcon sx={{ mr: 2, color: 'text.secondary', flexShrink: 0 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            {menu.imageUrl ? (
              <Box
                component="img"
                src={process.env.REACT_APP_CLOUDFRONT_URL + menu.imageUrl}
                alt={menu.name}
                sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1, mr: 2 }}
              />
            ) : null}
            <ListItemText
              primary={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {menu.name}
                  {menu.isSuspended && (
                    <Chip
                      label={
                        menu.suspendedUntil
                          ? `売り切れ（〜${formatSuspendedUntil(menu.suspendedUntil)} 自動再開）`
                          : '売り切れ'
                      }
                      color={menu.suspendedUntil ? 'warning' : 'error'}
                      size="small"
                    />
                  )}
                </Box>
              }
              secondary={formatPrice(menu.price)}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Box>
          <IconButton
            edge="end"
            aria-label={menu.isSuspended ? 'resume' : 'suspend'}
            title={menu.isSuspended ? '受注を再開' : '受注を停止（売り切れ）'}
            onClick={() => onToggleSuspension(menu)}
            sx={{ ml: 1 }}
            color={menu.isSuspended ? 'success' : 'default'}
          >
            {menu.isSuspended ? <PlayArrowIcon /> : <BlockIcon />}
          </IconButton>
          <IconButton
            edge="end"
            aria-label="edit"
            onClick={handleEditStart}
            sx={{ ml: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            edge="end"
            aria-label="delete"
            onClick={() => handleOpenDeleteModal(menu.id)}
            sx={{ ml: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )}

      {menuGroups && menuGroups.length > 0 && (
        <Accordion
          expanded={expanded}
          onChange={handleAccordionChange}
          sx={{ width: '100%', mt: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              メニューオプション設定
              {menu.menuOptionGroupId && (
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ ml: 1 }}
                >
                  (設定済み)
                </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl fullWidth>
              <InputLabel id={`menu-group-select-label-${menu.id}`}>
                メニューオプション
              </InputLabel>
              <Select
                labelId={`menu-group-select-label-${menu.id}`}
                id={`menu-group-select-${menu.id}`}
                value={selectedGroupId}
                label="メニューオプション"
                onChange={handleGroupSelect}
              >
                {menuGroups.map((group) => (
                  <MuiMenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
            {menu.menuOptionGroupId ? (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDetachGroup}
                startIcon={<LinkOffIcon />}
                sx={{ mt: 2 }}
                fullWidth
              >
                解除
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveGroup}
                disabled={!selectedGroupId}
                startIcon={<SaveIcon />}
                sx={{ mt: 2 }}
                fullWidth
              >
                保存
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </ListItem>
  );
};

const MenuManagement: React.FC<MenuManagementProps> = ({
  newMenuName,
  newMenuPrice,
  setNewMenuName,
  setNewMenuPrice,
}) => {
  const { store_id } = useParams<{ store_id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const menus = useSelector((state: RootState) => state.menu.menus);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [preview, setPreview] = useState<string>('');
  const [menuGroups, setMenuGroups] = useState<MenuOptionGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState<string | null>(null);
  const [menuToSuspend, setMenuToSuspend] = useState<MenuItem | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchData = async () => {
      if (store_id) {
        const groupsAction = await dispatch(fetchMenuGroups(store_id));
        if (fetchMenuGroups.fulfilled.match(groupsAction)) {
          setMenuGroups(groupsAction.payload);
        }
      }
    };
    fetchData();
  }, [dispatch, store_id]);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        setPreview(URL.createObjectURL(file));
        // 選び直し時はアップロード済みキーへ上書き（ADR 0065）
        handleImageChange(file, store_id!, dispatch, setImageUrl, imageUrl);
      }
    },
    [dispatch, store_id, imageUrl]
  );

  const onFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && store_id) {
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(file);

        // Upload to S3（選び直し時はアップロード済みキーへ上書き、ADR 0065）
        await handleImageChange(file, store_id, dispatch, setImageUrl, imageUrl);
      }
    },
    [dispatch, store_id, imageUrl]
  );

  const handleAddMenu = useCallback(
    async () => {
      if (!store_id || !newMenuName || newMenuPrice === '') return;

      const resultAction = await dispatch(
        addMenu({
          storeId: store_id,
          name: newMenuName,
          price: Number(newMenuPrice),
          imageUrl: imageUrl || null,
        })
      );

      if (addMenu.fulfilled.match(resultAction)) {
        setNewMenuName('');
        setNewMenuPrice('');
        setImageUrl('');
        setPreview('');
        setError(null);
        dispatch(fetchMenus(store_id));
      }
    },
    [dispatch, store_id, newMenuName, newMenuPrice, imageUrl, setNewMenuName, setNewMenuPrice]
  );

  const handleOpenDeleteModal = useCallback((id: string) => {
    setMenuToDelete(id);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setMenuToDelete(null);
  }, []);

  const handleDeleteMenuInternal = useCallback(
    async (id: string) => {
      if (!store_id) return;
      try {
        await dispatch(deleteMenu({ storeId: store_id, menuId: id })).unwrap();
        setError(null);
      } catch {
        setError('メニューの削除に失敗しました。');
      }
      handleCloseDeleteModal();
    },
    [dispatch, store_id, handleCloseDeleteModal]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = menus.findIndex((menu) => menu.id === active.id);
      const newIndex = menus.findIndex((menu) => menu.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        dispatch(reorderMenusLocally({ fromIndex: oldIndex, toIndex: newIndex }));
        setHasChanges(true);
      }
    }
  };

  const handleSaveOrder = useCallback(
    async () => {
      if (!store_id || !hasChanges) return;
      try {
        // 保存前に現在の順序でsortIndexを明示的に更新
        const updatedMenusWithSortIndex = menus.map((menu, index) => ({
          ...menu,
          sortIndex: index + 1
        }));

        await dispatch(
          reorderMenus({
            storeId: store_id,
            updatedMenus: updatedMenusWithSortIndex
          })
        ).unwrap();

        // 保存成功後、最新データを再取得して順序を確実に同期
        await dispatch(fetchMenus(store_id)).unwrap();
        setHasChanges(false);
        setError(null);
      } catch {
        setError('メニューの並び替えの保存に失敗しました。');
      }
    },
    [dispatch, store_id, hasChanges, menus]
  );

  const handleSaveMenuGroup = useCallback(
    async (menuId: string, groupId: string) => {
      if (store_id) {
        await dispatch(saveMenuGroup({ storeId: store_id, menuId, groupId }));
        dispatch(fetchMenus(store_id));
      }
    },
    [dispatch, store_id]
  );

  const handleDetachMenuGroup = useCallback(
    async (menuId: string, menuOptionGroupId: string) => {
      if (store_id) {
        await dispatch(detachMenuGroup({ storeId: store_id, menuId, menuOptionGroupId }));
        dispatch(fetchMenus(store_id));
      }
    },
    [dispatch, store_id]
  );

  // 停止中なら即再開、受付中なら恒久/日時指定を選ぶダイアログを開く
  const handleToggleSuspension = useCallback(
    async (menu: MenuItem) => {
      if (!store_id) return;
      if (menu.isSuspended) {
        try {
          await dispatch(
            updateMenuSuspension({
              storeId: store_id,
              menuId: menu.id,
              suspended: false,
              suspendedUntil: null,
            })
          ).unwrap();
          setError(null);
        } catch {
          setError('受注の再開に失敗しました。');
        }
      } else {
        setMenuToSuspend(menu);
      }
    },
    [dispatch, store_id]
  );

  const handleConfirmSuspension = useCallback(
    async (suspendedUntil: string | null) => {
      if (!store_id || !menuToSuspend) return;
      try {
        await dispatch(
          updateMenuSuspension({
            storeId: store_id,
            menuId: menuToSuspend.id,
            suspended: true,
            suspendedUntil,
          })
        ).unwrap();
        setError(null);
        setMenuToSuspend(null);
      } catch (e) {
        setMenuToSuspend(null);
        setError((e as Error)?.message || '受注の停止に失敗しました。');
      }
    },
    [dispatch, store_id, menuToSuspend]
  );

  const handleUpdateMenu = useCallback(
    async (updatedMenu: MenuItem) => {
      if (!store_id) return;
      try {
        // Assuming there's an updateMenu API function in menuApi.ts
        // If not, you would need to create one
        await dispatch(
          updateMenu({
            storeId: store_id,
            menuId: updatedMenu.id,
            name: updatedMenu.name,
            price: updatedMenu.price,
            // Keep other properties unchanged
            imageUrl: updatedMenu.imageUrl || null,
            menuOptionGroupId: updatedMenu.menuOptionGroupId || null
          })
        ).unwrap();
        setError(null);
        // Refresh menu list
        dispatch(fetchMenus(store_id));
      } catch {
        setError('メニューの更新に失敗しました。');
      }
    },
    [dispatch, store_id]
  );

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h6" gutterBottom>
        メニュー管理
      </Typography>

      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="menu-add-content"
          id="menu-add-header"
        >
          <Typography>メニュー追加</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="form" noValidate autoComplete="off">
            <TextField
              fullWidth
              label="メニュー名"
              value={newMenuName}
              onChange={(e) => setNewMenuName(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="価格（税抜・円）"
              type="text"
              value={newMenuPrice}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setNewMenuPrice(value === '' ? '' : Number(value));
              }}
              margin="normal"
              helperText="税抜金額を入力してください"
            />
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <input
                type="file"
                accept="image/*"
                onChange={onFileChange}
                style={{ display: 'none' }}
                id="menu-image-input"
                value=""
              />
              <label htmlFor="menu-image-input">
                {preview ? (
                  <Box
                    component="img"
                    src={preview}
                    alt="Preview"
                    sx={{ maxWidth: '100%', maxHeight: 200 }}
                  />
                ) : (
                  <Typography>
                    画像をドラッグ＆ドロップまたはクリックしてアップロード
                  </Typography>
                )}
              </label>
            </Box>
            <Button
              variant="contained"
              onClick={handleAddMenu}
              sx={{ mt: 2 }}
              fullWidth
              disabled={!newMenuName || !newMenuPrice}
            >
              追加
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">メニューリスト</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveOrder}
          startIcon={<SaveIcon />}
          disabled={!hasChanges}
        >
          並び順を保存
        </Button>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={menus.map((menu) => menu.id)}
          strategy={verticalListSortingStrategy}
        >
          <List sx={{ width: '100%' }}>
            {menus.map((menu) => (
              <DraggableMenuItem
                key={menu.id}
                menu={menu}
                handleDeleteMenuInternal={handleDeleteMenuInternal}
                menuGroups={menuGroups}
                onSaveMenuGroup={handleSaveMenuGroup}
                onDetachMenuGroup={handleDetachMenuGroup}
                onUpdateMenu={handleUpdateMenu}
                onToggleSuspension={handleToggleSuspension}
                dispatch={dispatch}
                store_id={store_id}
                handleOpenDeleteModal={handleOpenDeleteModal}
              />
            ))}
          </List>
        </SortableContext>
      </DndContext>

      {error && <Typography color="error">{error}</Typography>}

      {/* 受注停止ダイアログ */}
      <SuspensionDialog
        menu={menuToSuspend}
        onClose={() => setMenuToSuspend(null)}
        onConfirm={handleConfirmSuspension}
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-menu-dialog-title"
        aria-describedby="delete-menu-dialog-description"
      >
        <DialogTitle id="delete-menu-dialog-title">
          メニューの削除
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-menu-dialog-description">
            このメニューを削除してもよろしいですか？この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="primary">
            キャンセル
          </Button>
          <Button
            onClick={() => menuToDelete && handleDeleteMenuInternal(menuToDelete)}
            color="error"
            autoFocus
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuManagement;
